import React from "react";
import { RouteComponentProps } from "react-router";
import "./approve-modules.css";
import ApiService from "../../_services/ApiService";

const MODULE_STATUS = {
  OPTED: 0,
  JOIN_LATER: 4,
  TO_REGISTER: 11,
  REJECTED: 12
};

interface ElectiveChoice {
  termId: number;
  moduleCode: string;
  stream: string;
  moduleName: string;
  statusName: string;
  moduleId: number;
  advisorConsent?: {
    isApproved: boolean;
    remarks: string;
  };
  status: number;
}

interface ModuleRequest {
  moduleId: number;
  moduleCode: string;
  moduleName: string;
  stream: string;
  status: number;
  statusName: string;
  termId?: number;
  electives?: ElectiveChoice[];
}

interface Student {
  id: string;
  name: string;
  email: string;
  mobile: string;
  enrollmentId: number;
  moduleRequests: ModuleRequest[];
}

interface ApiResponse {
  status: number;
  data: Student[];
}

interface MyProps extends RouteComponentProps {}

type MyState = {
  students: Student[];
  selectedFilter: "all" | "approved" | "rejected" | "toReview" | "pending";
  searchQuery: string;
  loading: boolean;
  error: string | null;
  remarksModalOpen: boolean;
  remarksModuleId: number | null;
  remarksStudentId: string | null;
  remarksAction: "approve" | "reject" | "approveAll" | "rejectAll" | null;
  remarksText: string;
};

export class ApproveModules extends React.Component<MyProps, MyState> {
  state: MyState = {
    students: [],
    selectedFilter: "all",
    searchQuery: "",
    loading: true,
    error: null,
    remarksModalOpen: false,
    remarksModuleId: null,
    remarksStudentId: null,
    remarksAction: null,
    remarksText: "",
  };

  componentDidMount() {
    this.fetchStudents();
  }

  fetchStudents = async () => {
    try {
      this.setState({ loading: true, error: null });
      const response = await ApiService.getMenteesRegistration();
      const apiData = response.data as ApiResponse;

      if (apiData.status === 1 && apiData.data) {
        const students = apiData.data.map((student: any) => ({
          id: student.id,
          name: student.name,
          email: student.email,
          mobile: student.mobile,
          enrollmentId: student.enrollmentId,
          moduleRequests: student.moduleRequests || [],
        }));
        this.setState({ students, loading: false });
      } else {
        this.setState({ error: "Failed to load students", loading: false });
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      this.setState({ error: "Failed to load students", loading: false });
    }
  };

  getModuleStatus = (statusCode: number): "approved" | "rejected" | "toReview" => {
    if (statusCode === MODULE_STATUS.OPTED) return "approved";
    if (statusCode === MODULE_STATUS.REJECTED) return "rejected";
    if (statusCode === MODULE_STATUS.TO_REGISTER) return "toReview";
    return "toReview";
  };

  getFilteredStudents = () => {
    let filtered: Student[] = [];

    // Filter students based on module status
    if (this.state.selectedFilter === "all") {
      filtered = this.state.students;
    } else if (this.state.selectedFilter === "pending") {
      // Show students with no module requests
      filtered = this.state.students.filter((s) => s.moduleRequests.length === 0);
    } else {
      filtered = this.state.students.filter((student) =>
        student.moduleRequests.some((module) => {
          const mappedStatus = this.getModuleStatus(module.status);
          return mappedStatus === this.state.selectedFilter;
        })
      );
    }

    // Apply search query
    if (this.state.searchQuery.trim()) {
      const query = this.state.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (student) =>
          student.name.toLowerCase().includes(query) ||
          student.email.toLowerCase().includes(query) ||
          student.id.toLowerCase().includes(query) ||
          student.mobile.toLowerCase().includes(query) ||
          student.moduleRequests.some((module) =>
            module.moduleName.toLowerCase().includes(query) ||
            module.moduleCode.toLowerCase().includes(query)
          )
      );
    }

    return filtered;
  };

  getModuleCount = (status: string) => {
    if (status === "all") {
      return this.state.students.length;
    }
    if (status === "pending") {
      return this.state.students.filter((s) => s.moduleRequests.length === 0).length;
    }
    return this.state.students.reduce(
      (count, student) =>
        count +
        student.moduleRequests.filter((module) =>
          this.getModuleStatus(module.status) === status
        ).length,
      0
    );
  };

  openRemarksModal = (studentId: string, moduleId: number | null, action: "approve" | "reject" | "approveAll" | "rejectAll") => {
    this.setState({
      remarksModalOpen: true,
      remarksStudentId: studentId,
      remarksModuleId: moduleId,
      remarksAction: action,
      remarksText: "",
    });
  };

  closeRemarksModal = () => {
    this.setState({
      remarksModalOpen: false,
      remarksStudentId: null,
      remarksModuleId: null,
      remarksAction: null,
      remarksText: "",
    });
  };

  submitRemarks = async () => {
    const { remarksStudentId, remarksModuleId, remarksAction, remarksText } = this.state;

    if (!remarksStudentId || !remarksAction) return;

    if (remarksAction === "approve") {
      if (remarksModuleId !== null) {
        await this.handleApproveModule(remarksStudentId, remarksModuleId, remarksText);
      }
    } else if (remarksAction === "reject") {
      if (remarksModuleId !== null) {
        await this.handleRejectModule(remarksStudentId, remarksModuleId, remarksText);
      }
    } else if (remarksAction === "approveAll") {
      await this.handleApproveAllForStudent(remarksStudentId, remarksText);
    } else if (remarksAction === "rejectAll") {
      await this.handleRejectAllForStudent(remarksStudentId, remarksText);
    }

    this.closeRemarksModal();
  };

  handleApproveModule = async (studentId: string, moduleId: number, remarks: string = "") => {
    const student = this.state.students.find((s) => s.id === studentId);
    // Find module directly or via electives
    let module = student?.moduleRequests.find((m) => m.moduleId === moduleId);
    let parentModule: ModuleRequest | undefined;
    if (!module) {
      parentModule = student?.moduleRequests.find((m) => m.electives?.[0]?.moduleId === moduleId);
      module = parentModule?.electives?.[0] as any;
    }

    if (!student || !module) return;

    try {
      const payload = {
        enrollmentId: student.enrollmentId.toString(),
        moduleIds: [module.moduleId.toString()],
        termId: module.termId?.toString() || "",
        advisorRemarks: remarks,
        isApproved: true,
      };

      const response = await ApiService.confirmStudentModule(payload);

      if (response.data.status === 1) {
        const updatedStudents = this.state.students.map((s) => {
          if (s.id === studentId) {
            return {
              ...s,
              moduleRequests: s.moduleRequests.map((m) => {
                if (m.moduleId === moduleId) {
                  return { ...m, status: MODULE_STATUS.OPTED, statusName: "Opted" };
                }
                if (m.electives?.[0]?.moduleId === moduleId) {
                  return {
                    ...m,
                    electives: [{ ...m.electives[0], status: MODULE_STATUS.OPTED, statusName: "Opted" }],
                  };
                }
                return m;
              }),
            };
          }
          return s;
        });
        this.setState({ students: updatedStudents });
      } else {
        alert("Failed to approve module. Please try again.");
      }
    } catch (error) {
      console.error("Error approving module:", error);
      alert("Failed to approve module. Please try again.");
    }
  };

  handleRejectModule = async (studentId: string, moduleId: number, reason: string) => {
    const student = this.state.students.find((s) => s.id === studentId);
    // Find module directly or via electives
    let module = student?.moduleRequests.find((m) => m.moduleId === moduleId);
    let parentModule: ModuleRequest | undefined;
    if (!module) {
      parentModule = student?.moduleRequests.find((m) => m.electives?.[0]?.moduleId === moduleId);
      module = parentModule?.electives?.[0] as any;
    }

    if (!student || !module) return;

    try {
      const payload = {
        enrollmentId: student.enrollmentId.toString(),
        moduleIds: [module.moduleId.toString()],
        termId: module.termId?.toString() || "",
        advisorRemarks: reason,
        isApproved: false,
      };

      const response = await ApiService.confirmStudentModule(payload);

      if (response.data.status === 1) {
        const updatedStudents = this.state.students.map((s) => {
          if (s.id === studentId) {
            return {
              ...s,
              moduleRequests: s.moduleRequests.map((m) => {
                if (m.moduleId === moduleId) {
                  return { ...m, status: MODULE_STATUS.REJECTED, statusName: "Rejected" };
                }
                if (m.electives?.[0]?.moduleId === moduleId) {
                  return {
                    ...m,
                    electives: [{ ...m.electives[0], status: MODULE_STATUS.REJECTED, statusName: "Rejected" }],
                  };
                }
                return m;
              }),
            };
          }
          return s;
        });
        this.setState({ students: updatedStudents });
      } else {
        alert("Failed to reject module. Please try again.");
      }
    } catch (error) {
      console.error("Error rejecting module:", error);
      alert("Failed to reject module. Please try again.");
    }
  };

  handleApproveAllForStudent = async (studentId: string, remarks: string = "") => {
    const student = this.state.students.find((s) => s.id === studentId);
    if (!student) return;

    const pendingModules = student.moduleRequests.filter(
      (m) => m.status === MODULE_STATUS.TO_REGISTER
    );

    if (pendingModules.length === 0) return;

    try {
      const payload = {
        enrollmentId: student.enrollmentId.toString(),
        moduleIds: pendingModules.map((m) => m.moduleId.toString()),
        termId: pendingModules[0]?.termId?.toString() || "",
        advisorRemarks: remarks,
        isApproved: true,
      };

      const response = await ApiService.confirmStudentModule(payload);

      const allSuccess = response.data.status === 1;

      if (allSuccess) {
        const updatedStudents = this.state.students.map((s) => {
          if (s.id === studentId) {
            return {
              ...s,
              moduleRequests: s.moduleRequests.map((m) =>
                m.status === MODULE_STATUS.TO_REGISTER
                  ? {
                      ...m,
                      status: MODULE_STATUS.OPTED,
                      statusName: "Opted",
                    }
                  : m
              ),
            };
          }
          return s;
        });
        this.setState({ students: updatedStudents });
      } else {
        alert("Some modules failed to approve. Please try again.");
      }
    } catch (error) {
      console.error("Error approving all modules:", error);
      alert("Failed to approve all modules. Please try again.");
    }
  };

  handleRejectAllForStudent = async (studentId: string, reason: string) => {
    const student = this.state.students.find((s) => s.id === studentId);
    if (!student) return;

    const pendingModules = student.moduleRequests.filter(
      (m) => m.status === MODULE_STATUS.TO_REGISTER
    );

    if (pendingModules.length === 0) return;

    try {
      const payload = {
        enrollmentId: student.enrollmentId.toString(),
        moduleIds: pendingModules.map((m) => m.moduleId.toString()),
        termId: pendingModules[0]?.termId?.toString() || "",
        advisorRemarks: reason,
        isApproved: false,
      };

      const response = await ApiService.confirmStudentModule(payload);

      const allSuccess = response.data.status === 1;

      if (allSuccess) {
        const updatedStudents = this.state.students.map((s) => {
          if (s.id === studentId) {
            return {
              ...s,
              moduleRequests: s.moduleRequests.map((m) =>
                m.status === MODULE_STATUS.TO_REGISTER
                  ? {
                      ...m,
                      status: MODULE_STATUS.REJECTED,
                      statusName: "Rejected",
                    }
                  : m
              ),
            };
          }
          return s;
        });
        this.setState({ students: updatedStudents });
      } else {
        alert("Some modules failed to reject. Please try again.");
      }
    } catch (error) {
      console.error("Error rejecting all modules:", error);
      alert("Failed to reject all modules. Please try again.");
    }
  };

  getFilteredModules = (modules: ModuleRequest[]) => {
    if (this.state.selectedFilter === "all") {
      return modules;
    }
    return modules.filter((module) =>
      this.getModuleStatus(module.status) === this.state.selectedFilter
    );
  };

  render() {
    if (this.state.loading) {
      return (
        <div className="approve-modules-container">
          <div className="approve-modules-header">
            <div className="header-content">
              <button
                className="back-button"
                onClick={() => this.props.history.goBack()}
              >
                ← Back
              </button>
              <h1 className="page-title">Approve Module Requests</h1>
            </div>
          </div>
          <div className="approve-modules-content">
            <div className="loading-state">
              <p>Loading students...</p>
            </div>
          </div>
        </div>
      );
    }

    if (this.state.error) {
      return (
        <div className="approve-modules-container">
          <div className="approve-modules-header">
            <div className="header-content">
              <button
                className="back-button"
                onClick={() => this.props.history.goBack()}
              >
                ← Back
              </button>
              <h1 className="page-title">Approve Module Requests</h1>
            </div>
          </div>
          <div className="approve-modules-content">
            <div className="error-state">
              <p>{this.state.error}</p>
              <button onClick={this.fetchStudents} className="retry-btn">
                Retry
              </button>
            </div>
          </div>
        </div>
      );
    }

    const filteredStudents = this.getFilteredStudents();

    return (
      <div className="approve-modules-container">
        <div className="approve-modules-header">
          <div className="header-content">
            <button
              className="back-button"
              onClick={() => this.props.history.goBack()}
            >
              ← Back
            </button>
            <h1 className="page-title">Approve Module Requests</h1>
          </div>
        </div>

        <div className="approve-modules-content">
          {/* Filters Section */}
          <div className="filters-section">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search by name, email, ID, mobile, or module..."
                value={this.state.searchQuery}
                onChange={(e) => this.setState({ searchQuery: e.target.value })}
                className="search-input"
              />
            </div>

            <div className="status-filters">
              <button
                className={`filter-btn ${
                  this.state.selectedFilter === "all" ? "active" : ""
                }`}
                onClick={() => this.setState({ selectedFilter: "all" })}
              >
                All <span className="count">{this.getModuleCount("all")}</span>
              </button>
              <button
                className={`filter-btn ${
                  this.state.selectedFilter === "toReview" ? "active" : ""
                }`}
                onClick={() => this.setState({ selectedFilter: "toReview" })}
              >
                To Review{" "}
                <span className="count">{this.getModuleCount("toReview")}</span>
              </button>
              <button
                className={`filter-btn ${
                  this.state.selectedFilter === "approved" ? "active" : ""
                }`}
                onClick={() => this.setState({ selectedFilter: "approved" })}
              >
                Approved{" "}
                <span className="count">{this.getModuleCount("approved")}</span>
              </button>
              <button
                className={`filter-btn ${
                  this.state.selectedFilter === "rejected" ? "active" : ""
                }`}
                onClick={() => this.setState({ selectedFilter: "rejected" })}
              >
                Rejected{" "}
                <span className="count">{this.getModuleCount("rejected")}</span>
              </button>
              <button
                className={`filter-btn ${
                  this.state.selectedFilter === "pending" ? "active" : ""
                }`}
                onClick={() => this.setState({ selectedFilter: "pending" })}
              >
                Pending{" "}
                <span className="count">{this.getModuleCount("pending")}</span>
              </button>
            </div>
          </div>

          {/* Students List */}
          <div className="students-list">
            {filteredStudents.length === 0 ? (
              <div className="no-results">
                <p>No module requests found matching your criteria.</p>
              </div>
            ) : (
              filteredStudents.map((student) => {
                const filteredModules = this.getFilteredModules(
                  student.moduleRequests
                );

                const pendingModules = filteredModules.filter(
                  (m) => m.status === MODULE_STATUS.TO_REGISTER
                );
                const hasPendingModules = pendingModules.length > 0;

                return (
                  <div key={student.id} className="student-card">
                    <div className="student-header">
                      <div className="student-info">
                        <h3 className="student-name">{student.name}</h3>
                        <p className="student-email">{student.email}</p>
                        <p className="student-id">ID: {student.id}</p>
                        {student.mobile && (
                          <p className="student-mobile">Mobile: {student.mobile}</p>
                        )}
                      </div>
                      {hasPendingModules && (
                        <div className="bulk-actions">
                          <button
                            className="bulk-approve-btn"
                            onClick={() =>
                              this.openRemarksModal(student.id, null, "approveAll")
                            }
                          >
                            ✓ Approve All
                          </button>
                          <button
                            className="bulk-reject-btn"
                            onClick={() =>
                              this.openRemarksModal(student.id, null, "rejectAll")
                            }
                          >
                            ✗ Reject All
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="modules-section">
                      {filteredModules.length === 0 ? (
                        <div className="no-modules">
                          <p>No module requests submitted yet.</p>
                        </div>
                      ) : (
                        <>
                          <h4 className="modules-title">Module Requests:</h4>
                          <ul className="modules-list">
                            {filteredModules.map((module) => {
                              // Check if this is an elective module
                              const elective = module.electives?.[0];
                              const displayModule = elective || module;
                              const moduleIdForApi = elective ? elective.moduleId : module.moduleId;
                              const mappedStatus = this.getModuleStatus(displayModule.status);

                              return (
                                <li key={module.moduleId} className="module-item">
                                  <div className="module-info">
                                    <div className="module-header">
                                      <div className="module-name-wrapper">
                                        {elective ? (
                                          <>
                                            <span className="module-name">
                                              {module.moduleName}: {elective.moduleName}
                                            </span>
                                            <span className="module-code">
                                              {elective.moduleCode}
                                            </span>
                                          </>
                                        ) : (
                                          <>
                                            <span className="module-name">
                                              {module.moduleName}
                                            </span>
                                            <span className="module-code">
                                              {module.moduleCode}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                      <div className="module-header-right">
                                        <span
                                          className={`status-badge ${mappedStatus}`}
                                        >
                                          {displayModule.statusName}
                                        </span>
                                        {displayModule.status === MODULE_STATUS.TO_REGISTER && (
                                          <div className="module-quick-actions">
                                            <button
                                              className="quick-approve-btn"
                                              onClick={() =>
                                                this.openRemarksModal(
                                                  student.id,
                                                  moduleIdForApi,
                                                  "approve"
                                                )
                                              }
                                              title="Approve"
                                            >
                                              ✓
                                            </button>
                                            <button
                                              className="quick-reject-btn"
                                              onClick={() =>
                                                this.openRemarksModal(
                                                  student.id,
                                                  moduleIdForApi,
                                                  "reject"
                                                )
                                              }
                                              title="Reject"
                                            >
                                              ✗
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {displayModule.stream && (
                                      <span className="module-stream">
                                        Stream: {displayModule.stream}
                                      </span>
                                    )}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Remarks Modal */}
        {this.state.remarksModalOpen && (
          <div className="modal-overlay" role="presentation" onClick={this.closeRemarksModal}>
            <div className="modal-content" role="alertdialog" aria-modal="true" aria-labelledby="approve-reject-module-title" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 id="approve-reject-module-title">
                  {this.state.remarksAction === "approve" && "Approve Module"}
                  {this.state.remarksAction === "reject" && "Reject Module"}
                  {this.state.remarksAction === "approveAll" && "Approve All Modules"}
                  {this.state.remarksAction === "rejectAll" && "Reject All Modules"}
                </h3>
                <button className="modal-close-btn" onClick={this.closeRemarksModal}>
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <label htmlFor="remarks-input" className="remarks-label">
                  Remarks {this.state.remarksAction?.includes("reject") ? "(Required)" : "(Optional)"}:
                </label>
                <textarea
                  id="remarks-input"
                  className="remarks-textarea"
                  placeholder="Enter your remarks here..."
                  value={this.state.remarksText}
                  onChange={(e) => this.setState({ remarksText: e.target.value })}
                  rows={4}
                />
              </div>
              <div className="modal-footer">
                <button className="modal-cancel-btn" onClick={this.closeRemarksModal}>
                  Cancel
                </button>
                <button
                  className="modal-submit-btn"
                  onClick={this.submitRemarks}
                  disabled={
                    this.state.remarksAction?.includes("reject") &&
                    !this.state.remarksText.trim()
                  }
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}
